import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'

// Layouts
const AppLayout = () => import('../layouts/AppLayout.vue')
const PublicLayout = () => import('../layouts/PublicLayout.vue')

// Lazy-loaded route components
const Home = () => import('../views/public/Index.vue')
const Pricing = () => import('../views/public/Pricing.vue')
const Privacy = () => import('../views/public/Privacy.vue')
const Terms = () => import('../views/public/Terms.vue')

// Product pages
const AnnouncementsProduct = () => import('../views/public/products/Announcements.vue')
const BrbProduct = () => import('../views/public/products/BRB.vue')
const FlashbacksProduct = () => import('../views/public/products/Flashbacks.vue')
const QProduct = () => import('../views/public/products/Q.vue')
const ShoutoutsProduct = () => import('../views/public/products/Shoutouts.vue')
const TranslateProduct = () => import('../views/public/products/Translate.vue')
const CrossroadsProduct = () => import('../views/public/products/Crossroads.vue')
const GoalsProduct = () => import('../views/public/products/Goals.vue')

// Public Q Queue page
const PublicQueue = () => import('../views/public/PublicQueue.vue')

// Auth pages
const Login = () => import('../views/auth/Login.vue')
const Register = () => import('../views/auth/Register.vue')
const AuthCallback = () => import('../views/auth/QAuth.vue')

// Portal pages (authenticated)
const Dashboard = () => import('../views/portal/Dashboard.vue')
const Account = () => import('../views/portal/Account.vue')
const Announcements = () => import('../views/portal/Announcements.vue')
const Brb = () => import('../views/portal/BRB.vue')
const Flashbacks = () => import('../views/portal/Flashbacks.vue')
const Q = () => import('../views/portal/Q.vue')
const Shoutouts = () => import('../views/portal/Shoutouts.vue')
const Translate = () => import('../views/portal/Translate.vue')
const Crossroads = () => import('../views/portal/Crossroads.vue')
const Goals = () => import('../views/portal/Goals.vue')
const Loot = () => import('../views/portal/Loot.vue')
const Timekeeper = () => import('../views/portal/Timekeeper.vue')
const Billing = () => import('../views/portal/Billing.vue')
const Support = () => import('../views/portal/Support.vue')
const Docs = () => import('../views/portal/Docs.vue')
const DocsAnnouncements = () => import('../views/portal/docs/AnnouncementsDocs.vue')
const DocsBRB = () => import('../views/portal/docs/BRBDocs.vue')
const DocsFlashbacks = () => import('../views/portal/docs/FlashbacksDocs.vue')
const DocsQ = () => import('../views/portal/docs/QDocs.vue')
const DocsShoutouts = () => import('../views/portal/docs/ShoutoutsDocs.vue')
const DocsTranslate = () => import('../views/portal/docs/TranslateDocs.vue')
const DocsCrossroads = () => import('../views/portal/docs/CrossroadsDocs.vue')
const DocsGoals = () => import('../views/portal/docs/GoalsDocs.vue')
const DocsCustomCSS = () => import('../views/portal/docs/CustomCSSDocs.vue')

// Overlay pages (public with token)
const BrbOverlay = () => import('../views/overlays/BRBOverlay.vue')
const FlashbacksOverlay = () => import('../views/overlays/FlashbacksOverlay.vue')
const ShoutoutsOverlay = () => import('../views/overlays/ShoutoutsOverlay.vue')
const GoalsOverlay = () => import('../views/overlays/GoalsOverlay.vue')
const LootOverlay = () => import('../views/overlays/LootOverlay.vue')
const TimekeeperOverlay = () => import('../views/overlays/TimekeeperOverlay.vue')
const AuthErrorOverlay = () => import('../views/overlays/AuthErrorOverlay.vue')

const routes = [
  // Public routes
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { title: 'LiveLabs - Stream Management Tools' }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: Pricing,
    meta: { title: 'Pricing - LiveLabs' }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: Privacy,
    meta: { title: 'Privacy Policy - LiveLabs' }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: Terms,
    meta: { title: 'Terms of Service - LiveLabs' }
  },

  // Product pages
  {
    path: '/products/announcements',
    name: 'AnnouncementsProduct',
    component: AnnouncementsProduct,
    meta: { title: 'Announcements - LiveLabs' }
  },
  {
    path: '/products/brb',
    name: 'BrbProduct',
    component: BrbProduct,
    meta: { title: 'Be Right Back - LiveLabs' }
  },
  {
    path: '/products/flashbacks',
    name: 'FlashbacksProduct',
    component: FlashbacksProduct,
    meta: { title: 'Flashbacks - LiveLabs' }
  },
  {
    path: '/products/q',
    name: 'QProduct',
    component: QProduct,
    meta: { title: 'Q - Media Request Manager - LiveLabs' }
  },
  {
    path: '/products/shoutouts',
    name: 'ShoutoutsProduct',
    component: ShoutoutsProduct,
    meta: { title: 'Shoutouts - LiveLabs' }
  },
  {
    path: '/products/translate',
    name: 'TranslateProduct',
    component: TranslateProduct,
    meta: { title: 'Translate - LiveLabs' }
  },
  {
    path: '/products/crossroads',
    name: 'CrossroadsProduct',
    component: CrossroadsProduct,
    meta: { title: 'Crossroads - LiveLabs' }
  },
  {
    path: '/products/goals',
    name: 'GoalsProduct',
    component: GoalsProduct,
    meta: { title: 'Goals - LiveLabs' }
  },
  {
    path: '/products/loot',
    name: 'LootProduct',
    component: () => import('../views/public/products/Loot.vue'),
    meta: { title: 'Loot - LiveLabs' }
  },
  {
    path: '/products/timekeeper',
    name: 'TimekeeperProduct',
    component: () => import('../views/public/products/Timekeeper.vue'),
    meta: { title: 'Timekeeper - LiveLabs' }
  },

  // Public Q Queue page (viewer-facing)
  {
    path: '/q/:username',
    name: 'PublicQueue',
    component: PublicQueue,
    meta: { title: 'Queue - LiveLabs Q' }
  },

  // Auth routes
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: 'Login - LiveLabs', requiresGuest: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { title: 'Register - LiveLabs', requiresGuest: true }
  },
  {
    path: '/auth/q',
    name: 'AuthCallback',
    component: AuthCallback,
    meta: { title: 'Authenticating...' }
  },

  // Portal routes (require authentication) - with AppLayout
  {
    path: '/app',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: { title: 'Dashboard - LiveLabs', requiresAuth: true }
      },
      {
        path: 'account',
        name: 'Account',
        component: Account,
        meta: { title: 'Account - LiveLabs', requiresAuth: true }
      },
      {
        path: 'support',
        name: 'Support',
        component: Support,
        meta: { title: 'Support - LiveLabs', requiresAuth: true }
      },
      {
        path: 'announcements',
        name: 'Announcements',
        component: Announcements,
        meta: { title: 'Announcements - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'brb',
        name: 'Brb',
        component: Brb,
        meta: { title: 'Be Right Back - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'flashbacks',
        name: 'Flashbacks',
        component: Flashbacks,
        meta: { title: 'Flashbacks - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'q',
        name: 'Q',
        component: Q,
        meta: { title: 'Q - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'shoutouts',
        name: 'Shoutouts',
        component: Shoutouts,
        meta: { title: 'Shoutouts - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'timekeeper',
        name: 'Timekeeper',
        component: Timekeeper,
        meta: { title: 'Timekeeper - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'translate',
        name: 'Translate',
        component: Translate,
        meta: { title: 'Translate - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'crossroads',
        name: 'Crossroads',
        component: Crossroads,
        meta: { title: 'Crossroads - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'goals',
        name: 'Goals',
        component: Goals,
        meta: { title: 'Goals - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'loot',
        name: 'Loot',
        component: Loot,
        meta: { title: 'Loot - LiveLabs', requiresAuth: true, requiresPro: true }
      },
      {
        path: 'billing',
        name: 'Billing',
        component: Billing,
        meta: { title: 'Billing - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs',
        name: 'Docs',
        component: Docs,
        meta: { title: 'Documentation - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/announcements',
        name: 'DocsAnnouncements',
        component: DocsAnnouncements,
        meta: { title: 'Announcements Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/brb',
        name: 'DocsBRB',
        component: DocsBRB,
        meta: { title: 'BRB Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/flashbacks',
        name: 'DocsFlashbacks',
        component: DocsFlashbacks,
        meta: { title: 'Flashbacks Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/q',
        name: 'DocsQ',
        component: DocsQ,
        meta: { title: 'Q Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/shoutouts',
        name: 'DocsShoutouts',
        component: DocsShoutouts,
        meta: { title: 'Shoutouts Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/translate',
        name: 'DocsTranslate',
        component: DocsTranslate,
        meta: { title: 'Translate Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/crossroads',
        name: 'DocsCrossroads',
        component: DocsCrossroads,
        meta: { title: 'Crossroads Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/goals',
        name: 'DocsGoals',
        component: DocsGoals,
        meta: { title: 'Goals Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/loot',
        name: 'DocsLoot',
        component: () => import('../views/portal/docs/LootDocs.vue'),
        meta: { title: 'Loot Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/timekeeper',
        name: 'DocsTimekeeper',
        component: () => import('../views/portal/docs/TimekeeperDocs.vue'),
        meta: { title: 'Timekeeper Docs - LiveLabs', requiresAuth: true }
      },
      {
        path: 'docs/custom-css',
        name: 'DocsCustomCSS',
        component: DocsCustomCSS,
        meta: { title: 'Custom CSS Docs - LiveLabs', requiresAuth: true }
      }
    ]
  },

  // Overlay routes (public with token parameter)
  // Routes with /overlays/ prefix (for livelabs.tv/overlays/...)
  {
    path: '/overlays/brb/:token',
    name: 'BrbOverlay',
    component: BrbOverlay,
    meta: { title: 'Be Right Back Overlay', isOverlay: true }
  },
  {
    path: '/overlays/flashbacks/:token',
    name: 'FlashbacksOverlay',
    component: FlashbacksOverlay,
    meta: { title: 'Flashbacks Overlay', isOverlay: true }
  },
  {
    path: '/overlays/shoutouts/:token',
    name: 'ShoutoutsOverlay',
    component: ShoutoutsOverlay,
    meta: { title: 'Shoutouts Overlay', isOverlay: true }
  },
  {
    path: '/overlays/goals/:token',
    name: 'GoalsOverlay',
    component: GoalsOverlay,
    meta: { title: 'Goals Overlay', isOverlay: true }
  },
  {
    path: '/overlays/loot/:token',
    name: 'LootOverlay',
    component: LootOverlay,
    meta: { title: 'Loot Overlay', isOverlay: true }
  },
  {
    path: '/overlays/timekeeper/:token',
    name: 'TimekeeperOverlay',
    component: TimekeeperOverlay,
    meta: { title: 'Timekeeper Overlay', isOverlay: true }
  },
  {
    // Shared transparent overlay shown when any product overlay detects an
    // auth failure (logged out, missing OAuth scope, invalid token). Routed
    // here by useOverlayAuthGuard with ?product= and ?reason= query params.
    path: '/overlays/auth-error',
    name: 'AuthErrorOverlay',
    component: AuthErrorOverlay,
    meta: { title: 'LiveLabs - Re-link Required', isOverlay: true }
  },
  // Catch-all 404 route
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue'),
    meta: { title: '404 - Page Not Found' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Set page title
  document.title = to.meta.title || 'LiveLabs'

  // Skip authentication for overlay routes (they use token-based auth)
  if (to.meta.isOverlay) {
    return next()
  }

  // Initialize auth state if not already done
  if (!authStore.initialized) {
    await authStore.checkAuth()
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Redirect to regular login with return url
      return next({
        name: 'Login',
        query: { redirect: to.fullPath }
      })
    }
  }

  // Check if route requires Pro subscription
  if (to.meta.requiresPro) {
    if (authStore.subscriptionTier !== 'pro') {
      return next({ name: 'Billing' })
    }
  }

  // Redirect authenticated users away from guest-only pages
  // EXCEPT when sessionId is present (Q desktop app re-authentication flow)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    // Allow Q desktop app to re-authenticate with new scopes even when already logged in
    if (to.query.sessionId) {
      return next()
    }
    return next({ name: 'Dashboard' })
  }

  next()
})

// Global error handler
router.onError((error) => {
  console.error('Router error:', error)
})

export default router
